import { z } from "zod";
import { INDUSTRY_OPTIONS, MAX_NOTIFICATION_EMAILS, TECHNOLOGY_OPTIONS, } from "@kansai-ai-hub/types";
import { passwordSchema } from "./auth.js";
/**
 * 通知メール送信先。アプリ内通知の発火時、この User 宛通知をここに登録したメールへも
 * ACS Email で配信する。
 *
 * 最大 MAX_NOTIFICATION_EMAILS 件。各要素は「空文字 or 有効なメール」を許容し、フォームで
 * 行追加した直後の空欄行が送信をブロックしないようにする（空欄・重複・大小文字差はサーバ側で
 * 除去・正規化する）。preprocess/transform は使わない（input/output 型が割れて
 * react-hook-form の resolver 型が合わなくなるため）。
 *
 * **利用申込フォーム（inquiry.ts）と共有する。** 片方だけ緩いと、申込では通るのに
 * 承認してアカウントを作る段になって createUserSchema で弾かれ、事務局が申込内容を
 * 手で直す手段も無いまま詰む。
 */
export const notificationEmailsSchema = z
    .array(
// 各要素は trim 後に「空 or 有効なメール」を許容する（前後空白・空欄行を許す）。
// transform は使わず refine のみ（input/output 型を string に保ち RHF resolver の型整合を保つ）。
z.string().max(320).refine((s) => {
    const t = s.trim();
    return t === "" || z.string().email().safeParse(t).success;
}, "有効なメールアドレスを入力してください"))
    // 上限は「実メール件数」で数える（空欄行は計数しない）。フォームで行を足しただけの空欄が
    // 上限に食い込まないようにするため、`.max()` ではなく非空要素数で判定する。
    .refine((arr) => arr.filter((s) => s.trim() !== "").length <= MAX_NOTIFICATION_EMAILS, `通知メールは最大${MAX_NOTIFICATION_EMAILS}件までです`)
    .optional();
/**
 * 新規ユーザー発行（ADMIN 専用）。ENTERPRISE / MEMBER / ADMIN を発行できる。
 *
 * **ADMIN も作れる。** 以前は権限昇格防止として弾いていたが、事務局アカウントの
 * 発行手段が運用スクリプトしか無く、増員のたびにコンテナへ入って叩く運用になっていた。
 * 呼べるのは `@Roles("ADMIN")` のエンドポイントだけなので、権限の無い人が
 * 自分を引き上げる経路にはならない（判断の根拠は `admin.spec.ts` の該当テスト）。
 *
 * ただし「正規の ADMIN が離任前に／盗まれたセッションで 2 人目の ADMIN を仕込む」は
 * 防げない。そこで ADMIN 発行だけ **`currentPassword` による再認証**を必須にし、
 * 発行後は**既存の全 ADMIN へ通知**する（`AdminUsersService`）。前者はセッション単体での
 * 発行を止め、後者は「黙って増やす」を封じる。
 */
export const createUserSchema = z.object({
    email: z.string().email("有効なメールアドレスを入力してください"),
    name: z.string().min(1, "氏名を入力してください").max(60),
    password: passwordSchema,
    /*
     * **ADMIN も作れる。** 事務局アカウントの発行手段が運用スクリプトしか無く、
     * 増員のたびにコンテナへ入って叩く運用になっていた。
     * 発行できるのは ADMIN だけ（`POST /admin/users` は `@Roles("ADMIN")`）で、
     * 誰が誰を作ったかは `ActivityLog` に残る。
     */
    role: z.enum(["ENTERPRISE", "MEMBER", "ADMIN"]),
    /*
     * **ADMIN を作るときだけ必須の、発行者本人の再認証パスワード。**
     *
     * 事務局アカウントは管理画面の全操作ができる。盗まれたセッション 1 回で 2 人目の
     * ADMIN を作られると、元のアカウントを止めても権限が残り続ける（消せない自己複製）。
     * ここで発行者自身のパスワードを再入力させ、セッションだけでは ADMIN を作れなくする。
     *
     * **必須化の判定と照合はサーバ側**（`AdminUsersService.create`）で行う。ここを
     * `.superRefine` にすると `createUserSchema` が `ZodObject` でなくなり、
     * フロントの `registerFormSchema`（`.extend` している）が壊れるため、
     * 型の上では任意フィールドに留める。
     */
    currentPassword: z.string().max(200).optional(),
    organizationName: z.string().min(1, "組織名を入力してください").max(120),
    // 企業名の公開可否。false = 非公開（会員に企業名・ロゴ・リンク・companyId を秘匿し、
    // 会員の企業一覧にも出さない。承認済み応募の案件のみ実名を開示）。既定は公開。
    // 主に ENTERPRISE 登録で使う（MEMBER 組織は案件を投稿しないため実害はない）。
    // `.optional()`（`.default()` ではない）にして input/output 型を一致させ、react-hook-form の
    // 型不整合を避ける。未指定時の既定 true はサーバ側（`input.isNamePublic ?? true`）で吸収する。
    isNamePublic: z.boolean().optional(),
    // 組織の業界・得意技術。`industries` は MEMBER では「得意とする業界」として
    // 会員ホームの「業界知見が活きる案件」レーンやプロフィール表示に使い、ENTERPRISE では
    // 「自社が属する業界」として企業登録フォームから受ける（どちらも `Organization.industries`）。
    // `technologies` は MEMBER 登録でのみ UI に出す。
    industries: z.array(z.enum(INDUSTRY_OPTIONS)).max(INDUSTRY_OPTIONS.length).optional(),
    technologies: z.array(z.enum(TECHNOLOGY_OPTIONS)).max(TECHNOLOGY_OPTIONS.length).optional(),
    // ロゴは「絶対URL（外部CDN等）」もしくは「自前 API が返す /api/... の相対パス」のいずれも受け付ける。
    // upload-logo エンドポイントが `/api/v1/companies/logos/...` を返す仕様なので、
    // ここを `z.string().url()` に固定すると Admin 登録時に必ず VALIDATION_FAILED になる。
    organizationLogo: z
        .string()
        .max(2048)
        .refine((v) => v === "" || /^https?:\/\//.test(v) || v.startsWith("/"), "有効なURLまたはパスを入力してください")
        .optional(),
    organizationDesc: z.string().max(1000).optional(),
    // `.url()` は javascript: / data: 等の危険スキームも通すため、http(s) を明示要求する
    homepageUrl: z
        .string()
        .url("有効なURLを入力してください")
        .max(500)
        .refine((v) => /^https?:\/\//.test(v), "http(s) で始まるURLを入力してください")
        .or(z.literal(""))
        .optional(),
    department: z.string().max(100).optional(),
    contactRole: z.string().max(100).optional(),
    // 通知メール送信先。アプリ内通知の発火時、この User 宛通知をここに登録したメールへも ACS Email で配信する。
    // 最大 MAX_NOTIFICATION_EMAILS 件。各要素は「空文字 or 有効なメール」を許容し、フォームで行追加した
    // 直後の空欄行が送信をブロックしないようにする（空欄・重複・大小文字差はサーバ側で除去・正規化する）。
    // preprocess/transform は使わない（input/output 型が割れて react-hook-form の resolver 型が合わなくなるため）。
    // パスワード確認(confirmPassword)はフロント専用のため共有スキーマには含めない（サーバへ送らない）。
    notificationEmails: notificationEmailsSchema,
});
/**
 * 組織の更新（ADMIN 専用・後から変更）。企業名の公開可否と表示名を扱う。
 *
 * どちらのフィールドも任意にして**部分更新**を許す（公開設定だけ / 名前だけ を別々に叩ける）。
 * 両方とも未指定のリクエストはサーバ側で 400 にする（Zod で表現すると `.refine` が必要になり、
 * `ZodObject` でなくなってフロントの `.extend` を壊すため、判定は controller に置く）。
 */
export const updateOrganizationSchema = z.object({
    isNamePublic: z.boolean().optional(),
    /**
     * 表示用の組織名。登録時 (`createUserSchema.organizationName`) と同じ 1〜120 文字。
     * `LOWER(TRIM(name))` の部分ユニーク index に合わせて trim 後に長さを検証する
     * （空白だけの入力を弾き、前後空白違いの重複も作らせない）。
     * 既存組織との重複判定（大小文字・前後空白を無視）は controller 側で行う。
     */
    name: z
        .string()
        .trim()
        .min(1, "組織名を入力してください")
        .max(120, "組織名は120文字以内で入力してください")
        .optional(),
});
/**
 * 事務局向け 組織一覧 (Enterprise / Member) のクエリ。
 *
 * 同じ shape を `/admin/organizations?role=ENTERPRISE` と `?role=MEMBER` の両方で使う。
 * filter のセマンティクスは role 依存:
 *   - has-activity: ENTERPRISE→案件あり / MEMBER→応募あり
 *   - no-activity:  ENTERPRISE→案件なし / MEMBER→応募なし
 *   - recent-30d:   登録から 30 日以内
 *
 * **filter は会員タブ専用**。企業タブは業界 (`industries`) での絞り込みに置き換えたため
 * UI からクイックフィルタを外しており、`role=ENTERPRISE` では Web が常に `all` を送る
 * （Web 経由ではブックマークに残った filter も無効。API を直叩きする場合のみ従来どおり効く）。
 */
export const adminOrganizationListSortField = z.enum([
    "name",
    "cases", // ENTERPRISE 専用 (MEMBER では指定不可だが受信側で applications にマップ)
    "applications",
    "created",
    "activity",
]);
export const adminOrganizationListFilter = z.enum([
    "all",
    "has-activity",
    "no-activity",
    "recent-30d",
]);
/**
 * pageSize の上限は通常画面用に 100 を想定するが、CSV エクスポートのチャンク取得で
 * もう少し大きく扱いたい場合は FE 側で 100 単位の chunked fetch を行う。
 * ここでは上限を 100 のままにしておき、CSV は複数ページ走査する設計に統一する。
 */
export const adminOrganizationListQuerySchema = z.object({
    // 組織一覧は企業／会員だけを扱う。事務局の組織は 1 つで、一覧に出す対象ではない
    // （`companies.service.ts` も `users: { some: { role: "ENTERPRISE" } }` で絞っている）。
    role: z.enum(["ENTERPRISE", "MEMBER"]),
    q: z.string().max(120).optional(),
    sort: adminOrganizationListSortField.default("name"),
    order: z.enum(["asc", "desc"]).default("asc"),
    filter: adminOrganizationListFilter.default("all"),
    /**
     * 業界での絞り込み。カンマ区切りで複数指定し、**いずれかに該当**すれば残す（OR）。
     * 会員向けの参加企業一覧の業界フィルタと同じ意味で、組織の `industries` に対して効く。
     * URL search param をそのまま受けるため配列ではなく CSV 文字列で持つ
     * （案件一覧 `useCaseListQuerySchema.industries` と同じ流儀）。
     */
    industries: z.string().max(1000).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
/**
 * UA（利用状況）メトリクスのクエリ（ADMIN 専用 `/admin/usage-metrics`）。
 * 指標の定義は spec/backend/usage-metrics.md が正本。
 *
 * - `range`: 集計期間。すべて 7 の倍数にして、当期間と前期間の曜日構成を一致させる。
 *   全指標（要約・内訳・一覧の期間内集計・推移）に一貫して効く。
 * - `role`: 集計対象のロール。`ALL` は企業 + 会員。分母（名簿）の段階で絞るため、
 *   要約・推移・内訳・一覧のすべてが同じ対象で計算される。
 *
 * 「休眠」の判定日数は固定（サーバ側の定数）で、クエリでは変えられない。
 */
export const usageMetricsRange = z.enum(["7d", "28d", "84d"]);
/** 集計期間の日数。API の集計と画面・モックの表示で共用する。 */
export const USAGE_METRICS_RANGE_DAYS = {
    "7d": 7,
    "28d": 28,
    "84d": 84,
};
/** 集計対象のロール。事務局（ADMIN）は常に対象外なので選べない。 */
export const usageMetricsRole = z.enum(["ALL", "ENTERPRISE", "MEMBER"]);
export const usageMetricsQuerySchema = z.object({
    range: usageMetricsRange.default("28d"),
    role: usageMetricsRole.default("ALL"),
});
/**
 * 推移を遡れる区間数の上限（7 日 × 520 = 約 10 年、12 週 × 520 = 約 120 年）。
 * 通常は記録開始日で止まるが、API は上限でも `hasOlder` を false にして画面の矢印を止める。
 */
export const USAGE_TREND_MAX_OFFSET = 520;
/**
 * 利用人数の推移のクエリ（`/admin/usage-metrics/trend`）。
 * - `offset`: 今日を含む区間を 0 とし、集計期間の長さずつ過去へ遡った区間数。
 */
export const usageTrendQuerySchema = usageMetricsQuerySchema.extend({
    offset: z.coerce.number().int().min(0).max(USAGE_TREND_MAX_OFFSET).default(0),
});
